import React, { useRef, useEffect, useCallback } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import Label from '../Label';
import { classNames } from 'primereact/utils';

const FormikMultiSelect = (props) => {
  const { labelText, isMandatory = false, formik, name, className, onChange, ...rest } = props;
  const multiSelectRef = useRef(null);

  const shouldFocusOnHide = useRef(false);

  const focusMultiSelect = useCallback(() => {
    if (typeof multiSelectRef.current?.focus === 'function') {
      multiSelectRef.current.focus();
    } else {
      const el = document.getElementById(name);
      const focusable = el?.querySelector('input, [tabindex]');
      (focusable ?? el)?.focus();
    }
  }, [name]);

  const handleGlobalKeyDown = useCallback(
    (e) => {
      const panel = document.querySelector('.p-multiselect-panel');
      if (!panel) return;

      const filterInput = panel.querySelector('.p-multiselect-filter');
      const items = Array.from(panel.querySelectorAll('.p-multiselect-item')).map((item) => {
        if (!item.hasAttribute('tabindex')) {
          item.setAttribute('tabindex', '0');
        }
        return item;
      });

      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (e.key === 'Escape' || (e.altKey && e.key === 'ArrowUp')) {
        shouldFocusOnHide.current = true;
        multiSelectRef.current?.hide();
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        const activeEl = document.activeElement;
        const isTargetInHeader = activeEl === filterInput || filterInput?.contains(activeEl);

        if (e.shiftKey) {
          if (isTargetInHeader) {
            lastItem?.focus();
          } else {
            filterInput?.focus();
          }
        } else {
          if (isTargetInHeader) {
            firstItem?.focus();
          } else {
            filterInput?.focus();
          }
        }
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const activeEl = document.activeElement;
        const currentIndex = items.indexOf(activeEl);

        if (e.key === 'ArrowDown') {
          if (activeEl === filterInput || currentIndex === -1) {
            e.preventDefault();
            firstItem?.focus();
          } else if (currentIndex < items.length - 1) {
            e.preventDefault();
            items[currentIndex + 1]?.focus();
          }
        } else if (e.key === 'ArrowUp') {
          if (currentIndex === 0) {
            e.preventDefault();
            filterInput ? filterInput.focus() : lastItem?.focus();
          } else if (currentIndex > 0) {
            e.preventDefault();
            items[currentIndex - 1]?.focus();
          } else if (activeEl === filterInput) {
            e.preventDefault();
            lastItem?.focus();
          }
        }
      }

      if (e.key === 'Space' || e.key === ' ') {
        const activeEl = document.activeElement;
        if (items.includes(activeEl)) {
          e.preventDefault();
          activeEl.click();
        }
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
        const selectAllCheckbox = panel.querySelector('.p-multiselect-header .p-checkbox-box');
        if (selectAllCheckbox) {
          selectAllCheckbox.click();
          e.preventDefault();
          e.stopPropagation();
        }
      }
    },
    [multiSelectRef]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [handleGlobalKeyDown]);

  const isInvalid = !!(formik.touched[name] && formik.errors[name]);
  const mergeClassName = classNames(className, {
    'p-invalid': isInvalid
  });

  const onKeyDown = (e) => {
    const isOverlayVisible = multiSelectRef.current?.overlayVisible;

    // Alt + Down to open
    if (e.altKey && e.key === 'ArrowDown') {
      multiSelectRef.current?.show();
      e.preventDefault();
      return;
    }

    if (e.key === 'Escape') {
      if (!isOverlayVisible) {
        formik.setFieldValue(name, [], true);
      }
      return;
    }

    if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation();

      const panel = document.querySelector('.p-multiselect-panel');
      if (panel) {
        const selectAllCheckbox = panel.querySelector('.p-multiselect-header .p-checkbox-box');
        if (selectAllCheckbox) {
          selectAllCheckbox.click();
          return;
        }
      }

      const valueKey = rest.optionValue;
      const allValues = rest.options?.map((opt) => (valueKey ? opt[valueKey] : opt)) || [];
      const currentValues = formik.values[name] || [];
      if (currentValues.length === allValues.length) {
        formik.setFieldValue(name, []);
      } else {
        formik.setFieldValue(name, allValues);
      }
      return;
    }

    if (e.key === 'Backspace' || e.key === 'Delete') {
      const values = formik.values[name] || [];
      const isTargetInsideTrigger = multiSelectRef.current?.container?.contains(e.target);

      if (values.length > 0 && !isOverlayVisible && isTargetInsideTrigger) {
        formik.setFieldValue(name, values.slice(0, -1));
        e.preventDefault();
        e.stopPropagation();
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      {labelText && <Label htmlFor={name} text={labelText} isMandatory={isMandatory} />}
      <MultiSelect
        {...rest}
        ref={multiSelectRef}
        id={name}
        name={name}
        value={formik.values[name]}
        onBlur={(e) => {
          formik.handleBlur(e);
          if (rest.onBlur) rest.onBlur(e);
        }}
        onKeyDownCapture={onKeyDown}
        onChange={(e) => {
          formik.setFieldValue(name, e.value, true);
          if (onChange) onChange(e);
        }}
        className={mergeClassName}
        filter
        display="chip"
        onShow={() => {
          document.addEventListener('keydown', handleGlobalKeyDown, true);
          setTimeout(() => {
            const panel = document.querySelector('.p-multiselect-panel');
            const items = panel?.querySelectorAll('.p-multiselect-item');
            if (items && items.length > 0) {
              items.forEach((item) => {
                if (!item.hasAttribute('tabindex')) {
                  item.setAttribute('tabindex', '0');
                }
              });
              const filterInput = panel.querySelector('.p-multiselect-filter');
              if (filterInput) {
                filterInput.focus();
              } else {
                items[0].focus();
              }
            }
          }, 50);
        }}
        onHide={() => {
          document.removeEventListener('keydown', handleGlobalKeyDown, true);
          if (shouldFocusOnHide.current) {
            shouldFocusOnHide.current = false;
            setTimeout(() => focusMultiSelect(), 0);
          }
        }}
      />
      {isInvalid && <small className="p-error">{formik.errors[name]}</small>}
    </>
  );
};

export default FormikMultiSelect;
